import { PayloadAction, createSlice, current } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { generateId } from 'utils/generateId';
import {
  ElementSide,
  ElementType,
  FooterElement,
  HeaderElement,
  PagePartType,
  Recipient,
  elementsTemplate,
} from './constants';
import { Element } from './constants';
import { Document } from 'dataTypes/document';
import {
  isApprovalTableElement,
  isCheckboxElement,
  isDateElement,
  isDropdownElement,
  isInitialsElement,
  isPricingTableElement,
  isQuoteAcceptanceInfoTableElement,
  isRadioButtonElement,
  isSignatureElement,
  isTextFieldElement,
} from 'utils/typeGuards';
export interface Column {
  type: PagePartType.Column;
  baseColumns: number;
  id: string;
  elements: Element[];
}

export interface RowSection {
  id: string;
  type: PagePartType.Row;
  columns: Column[];
}
export interface Page {
  id: string;
  isOrientedHorizontally: boolean;
  type: PagePartType.Page;
  header: HeaderElement;
  footer: FooterElement;
  isIndividualHeaderVisible: boolean;
  isIndividualFooterVisible: boolean;
  rows: RowSection[];
  addSheetPadding: boolean;
  pageBreaks: number;
  hideHeaderAndFooterOnOverflow: boolean;
  backgroundColor?: string;
  backgroundImageId?: string;
  overflowBackgroundImageId?: string;
  showBackgroundColor?: boolean;
  showBackgroundImage?: boolean;
  showOverflowBackgroundImage?: boolean;
  alignment?: string;
  backgroundImageOpacity?: number;
  isLegal?: boolean;
}

export interface SalesforceIds {
  opportunityId?: string;
  quoteId?: string;
  contractId?: string;
  loanId?: string;
}
export interface Template {
  pages: Page[];
  recipients: Recipient[];
  salesforceIds: SalesforceIds;
  isOrientedHorizontally: boolean;
}

export type selectedElement = {
  type: PagePartType.Element;
  pageId: string;
  index: number;
  data: Element;
  rowId: string;
  columnId: string;
};

type selectedRow = {
  type: PagePartType.Row;
  pageId: string;
  index: number;
  data: Element;
  rowId: string;
  columnId: string;
};

type ActionPayload<T extends keyof Page> = {
  id?: string;
  property: T;
  value: Page[T];
};

export type selectedHeader = {
  type: ElementType.Header;
  pageId: string;
  data: HeaderElement;
};

export type selectedFooter = {
  type: ElementType.Footer;
  pageId: string;
  data: FooterElement;
};

export type SelectedPage = {
  type: PagePartType.Page;
  pageId: string;
  data: Page;
};

export type SelectedItemType = selectedElement | selectedRow | selectedHeader | selectedFooter | SelectedPage;

export enum DocumentType {
  DOCUMENT = 'DOCUMENT',
  CONTRACT = 'CONTRACT',
  QUOTE = 'QUOTE',
  PRECLOSING = 'PRECLOSING',
}

interface DocumentTemplate {
  templateJson: Template;
  name: string;
  noteContent: string;
  noteTitle: string;
  selectedItem: SelectedItemType | null;
  selectedRecipient: Recipient | null;
  isSidebarOpen: boolean;
  initialDocument: Partial<Document>;
  type: DocumentType;
  savingMethod: 'TEMPLATE' | 'FILE';
  activePageId: string | null;
}

const getElementTemplate = (type: ElementType, state?: DocumentTemplate): Element => {
  const template = elementsTemplate[type];
  const elementTemplate = {
    ...template,
    id: generateId(),
  };
  if (state) {
    const { selectedRecipient } = current(state);
    if ('recipient' in elementTemplate && selectedRecipient) {
      elementTemplate.recipient = selectedRecipient;
      const recipientAction = {
        type: 'document/addElementIdToRecipient',
        payload: {
          recipientId: selectedRecipient.id,
          elementId: elementTemplate.id,
        },
      };
      documentSlice.caseReducers.addElementIdToRecipient(state, recipientAction);
      if (elementTemplate.content && 'labelText' in elementTemplate.content) {
        elementTemplate.content = {
          ...elementTemplate.content,
          labelText: `${selectedRecipient.fullName} ${type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()}.`,
        };
      } else if (isQuoteAcceptanceInfoTableElement(elementTemplate)) {
        elementTemplate.content = {
          ...elementTemplate.content,
          acceptanceInfo: {
            ...elementTemplate.content.acceptanceInfo,
            name: selectedRecipient.fullName,
          },
          table: {
            ...elementTemplate.content.table,
            cells: {
              ...elementTemplate.content.table.cells,
              'cell-name': {
                ...elementTemplate.content.table.cells['cell-name'],
                content: `${selectedRecipient.fullName}`,
              },
              'cell-date': {
                ...elementTemplate.content.table.cells['cell-date'],
                content: `${selectedRecipient.fullName} Date`,
              },
              'cell-signature': {
                ...elementTemplate.content.table.cells['cell-signature'],
                content: `${selectedRecipient.fullName} Signature`,
              },
            },
          },
        };
      }
    }
  }
  return elementTemplate;
};

const getBaseSection = (type: ElementType, state: DocumentTemplate): RowSection => {
  return {
    id: `row-${generateId()}`,
    type: PagePartType.Row,
    columns: [
      {
        type: PagePartType.Column,
        baseColumns: 6,
        id: `column-${generateId()}`,
        elements: [getElementTemplate(type, state)],
      },
    ],
  };
};

const getBasePage = (state: DocumentTemplate): Page => {
  return {
    id: `${generateId()}`,
    isOrientedHorizontally: false,
    type: PagePartType.Page,
    rows: [],
    header: getElementTemplate(ElementType.Header, state) as HeaderElement,
    footer: getElementTemplate(ElementType.Footer, state) as FooterElement,
    isIndividualHeaderVisible: true,
    isIndividualFooterVisible: true,
    backgroundColor: '#ffffff',
    showBackgroundColor: false,
    showBackgroundImage: false,
    alignment: 'center',
    backgroundImageOpacity: 1,
    addSheetPadding: true,
    pageBreaks: 0,
    hideHeaderAndFooterOnOverflow: false,
  };
};

const initialState: DocumentTemplate = {
  initialDocument: {
    noteContent: '',
    noteTitle: '',
    name: '',
    templateJson: '',
  },
  noteContent: '',
  noteTitle: '',
  name: '',
  selectedItem: null,
  selectedRecipient: null,
  isSidebarOpen: false,
  templateJson: {
    pages: [
      {
        id: generateId(),
        isOrientedHorizontally: false,
        type: PagePartType.Page,
        rows: [],
        header: getElementTemplate(ElementType.Header) as HeaderElement,
        footer: getElementTemplate(ElementType.Footer) as FooterElement,
        isIndividualHeaderVisible: true,
        isIndividualFooterVisible: true,
        backgroundColor: '#ffffff',
        showBackgroundColor: false,
        showBackgroundImage: false,
        alignment: 'center',
        backgroundImageOpacity: 1,
        addSheetPadding: true,
        pageBreaks: 0,
        hideHeaderAndFooterOnOverflow: false,
      },
    ],
    recipients: [],
    salesforceIds: {},
    isOrientedHorizontally: false,
  },
  type: DocumentType.DOCUMENT,
  savingMethod: 'FILE',
  activePageId: null,
};

const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },

    updateNoteContent(state, action) {
      const payload = action.payload;
      state.noteContent = payload;
    },
    updateNoteTitle(state, action) {
      const payload = action.payload;
      state.noteTitle = payload;
    },
    updateDocumentName(state, action) {
      const payload = action.payload;
      state.name = payload;
    },
    updateTemplateJson(state, action) {
      const { templateJson } = action.payload;
      const templateParsed = JSON.parse(templateJson);
      state.templateJson = templateParsed;
    },
    updateDocumentType(state, action) {
      const payload = action.payload;
      state.type = payload as DocumentType;
    },
    updateSelectedRecipient(state, action) {
      state.selectedRecipient = action.payload;
    },
    updateRecipients(state, action) {
      state.templateJson = { ...state.templateJson, recipients: action.payload };
    },
    updateTemplateJsonSalesforceIds(state, action) {
      const { opportunityId, quoteId, contractId, loanId } = action.payload;
      state.templateJson = { ...state.templateJson, salesforceIds: { opportunityId, quoteId, contractId, loanId } };
    },
    addElementIdToRecipient(state, action) {
      const { recipientId, elementId } = action.payload;

      const updatedRecipients = state.templateJson.recipients.map(recipient => {
        if (recipient.id === recipientId && recipient.elementsIds) {
          const updatedElementsIds = [...recipient.elementsIds, elementId];
          return { ...recipient, elementsIds: updatedElementsIds };
        }
        return recipient;
      });

      state.templateJson = { ...state.templateJson, recipients: updatedRecipients };
    },
    removeElementIdFromRecipients(state, action) {
      const elementIdToRemove = action.payload;

      const updatedRecipients = state.templateJson.recipients.map(recipient => {
        if (recipient.elementsIds) {
          const updatedElementsIds = recipient.elementsIds.filter(id => id !== elementIdToRemove);
          return { ...recipient, elementsIds: updatedElementsIds };
        }
        return recipient;
      });

      state.templateJson = { ...state.templateJson, recipients: updatedRecipients };
    },
    updateInitialDocument(state, action) {
      const { document } = action.payload;
      state.initialDocument = { ...document };
    },
    updateContentHeaderById(state, action) {
      const { content, style } = action.payload;
      if (!state.selectedItem) return;

      if (state.selectedItem.type === ElementType.Header) {
        const { pageId } = state.selectedItem;
        const page = findPageById(state.templateJson.pages, pageId);
        if (page) {
          page.header.content = { ...page.header.content, ...content };
          page.header.style = { ...page.header.style, ...style };
          state.selectedItem.data.content = { ...state.selectedItem.data.content, ...content };
          state.selectedItem.data.style = { ...state.selectedItem.data.style, ...style };
        }
      }
    },
    updateContentFooterById(state, action) {
      const { content, style } = action.payload;
      if (!state.selectedItem) return;

      if (state.selectedItem.type === ElementType.Footer) {
        const { pageId } = state.selectedItem;
        const page = findPageById(state.templateJson.pages, pageId);
        if (page) {
          page.footer.content = { ...page.footer.content, ...content };
          page.footer.style = { ...page.footer.style, ...style };
          state.selectedItem.data.content = { ...state.selectedItem.data.content, ...content };
          state.selectedItem.data.style = { ...state.selectedItem.data.style, ...style };
        }
      }
    },
    updateContentElementById(state, action) {
      const { content, style, recipient, recipients } = action.payload;
      if (!state.selectedItem || state.selectedItem.type !== PagePartType.Element) return;
      const { pageId, rowId, columnId, index } = state.selectedItem;
      const page = findPageById(state.templateJson.pages, pageId);
      if (page) {
        const element = page.rows.find(r => r.id === rowId)?.columns.find(c => c.id === columnId)?.elements[index];
        if (element) {
          element.content = { ...element.content, ...content };
          element.style = { ...element.style, ...style };

          if (
            isCheckboxElement(element) ||
            isTextFieldElement(element) ||
            isDateElement(element) ||
            isRadioButtonElement(element) ||
            isDropdownElement(element) ||
            isSignatureElement(element) ||
            isInitialsElement(element) ||
            isQuoteAcceptanceInfoTableElement(element)
          ) {
            element.recipient = { ...element.recipient, ...recipient };
            if (
              isCheckboxElement(state.selectedItem.data) ||
              isTextFieldElement(state.selectedItem.data) ||
              isDateElement(state.selectedItem.data) ||
              isRadioButtonElement(state.selectedItem.data) ||
              isDropdownElement(state.selectedItem.data) ||
              isSignatureElement(state.selectedItem.data) ||
              isInitialsElement(state.selectedItem.data) ||
              isQuoteAcceptanceInfoTableElement(state.selectedItem.data)
            ) {
              state.selectedItem.data.recipient = { ...element.recipient, ...recipient };
            }
          }

          if (isApprovalTableElement(element)) {
            element.recipients = { ...element.recipients, ...recipients };
            if (isApprovalTableElement(state.selectedItem.data)) {
              state.selectedItem.data.recipients = { ...element.recipients, ...recipients };
            }
          }

          state.selectedItem.data.content = { ...element.content, ...content };
          state.selectedItem.data.style = { ...element.style, ...style };
        }
      }
    },
    updateElementById(state, action) {
      const { content, style, recipient, elementId } = action.payload;

      const findElementById = (elementId: string, pages: Page[]) => {
        for (const page of pages) {
          for (const row of page.rows) {
            for (const column of row.columns) {
              const element = column.elements.find(e => e.id === elementId);
              if (element) {
                return element;
              }
            }
          }
        }
        return null;
      };

      const { pages } = state.templateJson;
      const element = findElementById(elementId, pages);

      if (element) {
        element.content = { ...element.content, ...content };
        element.style = { ...element.style, ...style };

        if (
          isCheckboxElement(element) ||
          isTextFieldElement(element) ||
          isDateElement(element) ||
          isRadioButtonElement(element) ||
          isDropdownElement(element) ||
          isSignatureElement(element) ||
          isInitialsElement(element)
        ) {
          element.recipient = { ...element.recipient, ...recipient };
        }
      }
    },
    toggleIndividualVisibilityHeader(state, action) {
      const { pageId, isVisible } = action.payload;
      const page = findPageById(state.templateJson.pages, pageId);
      if (page) {
        page.isIndividualHeaderVisible = !isVisible;
      }
    },

    toggleAllVisibilityHeader(state, action) {
      const { isVisible } = action.payload;
      state.templateJson.pages.forEach(page => {
        if (!page.hideHeaderAndFooterOnOverflow) {
          page.isIndividualHeaderVisible = !isVisible;
        }
      });
    },

    toggleIndividualVisibilityFooter(state, action) {
      const { pageId, isVisible } = action.payload;
      const page = findPageById(state.templateJson.pages, pageId);
      if (page) {
        page.isIndividualFooterVisible = !isVisible;
      }
    },

    toggleAllVisibilityFooter(state, action) {
      const { isVisible } = action.payload;
      state.templateJson.pages.forEach(page => {
        if (!page.hideHeaderAndFooterOnOverflow) {
          page.isIndividualFooterVisible = !isVisible;
        }
      });
    },
    addPage(state, action) {
      const { previousPageIndex } = action.payload;
      state.templateJson.pages.splice(previousPageIndex + 1, 0, getBasePage(state));
    },

    addPageSection(
      state,
      {
        payload,
      }: PayloadAction<{
        pageId: string;
        sectionId?: string;
        type: ElementType;
      }>
    ) {
      const { pageId, sectionId, type } = payload;
      const page = findPageById(state.templateJson.pages, pageId);
      if (page) {
        if (!sectionId) {
          page.rows = [getBaseSection(type, state), ...page.rows];
        } else {
          const index = page.rows.findIndex(rows => rows.id === sectionId);
          const sectionsArr = page.rows.slice();

          page.rows = [...sectionsArr.splice(0, index + 1), getBaseSection(type, state), ...sectionsArr];
        }
      }
    },

    addRowContentBlock(state, action) {
      const { pageId, rowIndex, side, type } = action.payload;
      const page = findPageById(state.templateJson.pages, pageId);
      const newRow = getBaseSection(type, state);
      if (page) {
        if (side === ElementSide.Top) {
          page.rows = [...page.rows.slice(0, rowIndex), newRow, ...page.rows.slice(rowIndex)];
        }
        if (side === ElementSide.Bottom) {
          page.rows = [...page.rows.slice(0, rowIndex + 1), newRow, ...page.rows.slice(rowIndex + 1)];
        }
      }
    },

    addElementContentBlock(state, action) {
      const { pageId, rowId, columnId, index, side, type } = action.payload;
      const droppablePage = findPageById(state.templateJson.pages, pageId);
      if (side === ElementSide.Bottom) {
        const droppableColumn = droppablePage?.rows
          .find(row => row.id === rowId)
          ?.columns.find(column => column.id === columnId);
        if (droppableColumn) {
          const elements = droppableColumn.elements;
          droppableColumn.elements = [
            ...elements.slice(0, index + 1),
            getElementTemplate(type, state),
            ...elements.slice(index + 1),
          ];
        }
      }
      if (side === ElementSide.Top) {
        const droppableColumn = droppablePage?.rows
          .find(row => row.id === rowId)
          ?.columns.find(column => column.id === columnId);
        if (droppableColumn) {
          const elements = droppableColumn.elements;
          droppableColumn.elements = [
            ...elements.slice(0, index),
            getElementTemplate(type, state),
            ...elements.slice(index),
          ];
        }
      }
      if (side === ElementSide.Left) {
        const droppableRow = droppablePage?.rows.find(row => row.id === rowId);
        const columnIndex = droppableRow?.columns.findIndex(column => column.id === columnId);
        if (droppableRow) {
          droppableRow.columns = [
            ...droppableRow.columns.slice(0, columnIndex),
            {
              baseColumns: 4,
              id: generateId(),
              type: PagePartType.Column,
              elements: [getElementTemplate(type, state)],
            },
            ...droppableRow.columns.slice(columnIndex),
          ];
        }
      }
      if (side === ElementSide.Right) {
        const droppableRow = droppablePage?.rows.find(row => row.id === rowId);
        const columnIndex = droppableRow?.columns.findIndex(column => column.id === columnId);
        if (droppableRow) {
          droppableRow.columns = [
            ...droppableRow.columns.slice(0, Number(columnIndex) + 1),
            {
              baseColumns: 4,
              type: PagePartType.Column,
              id: generateId(),
              elements: [getElementTemplate(type, state)],
            },
            ...droppableRow.columns.slice(Number(columnIndex) + 1),
          ];
        }
      }
    },
    saveSelectedItem(state, action) {
      const { data } = action.payload;
      if (data) {
        state.selectedItem = data;
      }
    },
    clearSelectedItem(state, _action) {
      state.selectedItem = null;
    },

    deleteElementById(state, action) {
      const { pageId, rowId, columnId, elementId } = action.payload;

      const originPage = findPageById(state.templateJson.pages, pageId);

      if (!originPage) {
        return;
      }

      const rowIndex = originPage.rows.findIndex(row => row.id === rowId);

      if (rowIndex === -1) {
        return;
      }

      const column = originPage.rows[rowIndex].columns.find(column => column.id === columnId);

      if (column) {
        column.elements = column.elements.filter(element => element.id !== elementId);

        const selectAction = {
          ...action,
          payload: elementId,
        };
        documentSlice.caseReducers.removeElementIdFromRecipients(state, selectAction);
      }
    },

    deleteColumnById(state, action) {
      const { pageId, rowId, columnId } = action.payload;
      const originPage = findPageById(state.templateJson.pages, pageId);
      const rowIndex = originPage?.rows.findIndex(row => row.id === rowId);
      if (originPage) {
        originPage.rows[Number(rowIndex)].columns = originPage.rows[Number(rowIndex)].columns.filter(
          column => column.id !== columnId
        );
      }
    },

    deleteRowById(state, action) {
      const { pageId, rowId } = action.payload;
      const originPage = findPageById(state.templateJson.pages, pageId);
      if (originPage) {
        originPage.rows = originPage.rows.filter(row => row.id !== rowId);
      }
    },

    moveElementitem(state, action) {
      if (!state.selectedItem || state.selectedItem.type !== PagePartType.Element) return;
      const { pageId, rowId, columnId, index, side } = action.payload;
      const {
        index: draggableIndex,
        pageId: draggablePageId,
        rowId: draggableRowId,
        columnId: draggableColumnId,
      } = state.selectedItem;

      const originPage = findPageById(state.templateJson.pages, draggablePageId);

      const droppablePage = findPageById(state.templateJson.pages, pageId);

      const movedElement = originPage?.rows
        .find(row => row.id === draggableRowId)
        ?.columns.find(column => column.id === draggableColumnId)
        ?.elements.splice(draggableIndex, 1);

      if (side === ElementSide.Bottom) {
        const droppableColumn = droppablePage?.rows
          .find(row => row.id === rowId)
          ?.columns.find(column => column.id === columnId);

        if (droppableColumn && movedElement) {
          const elements = droppableColumn.elements;
          droppableColumn.elements = [...elements.slice(0, index + 1), ...movedElement, ...elements.slice(index + 1)];
        }
      }
      if (side === ElementSide.Top) {
        const droppableColumn = droppablePage?.rows
          .find(row => row.id === rowId)
          ?.columns.find(column => column.id === columnId);
        if (droppableColumn && movedElement) {
          const elements = droppableColumn.elements;
          droppableColumn.elements = [...elements.slice(0, index), ...movedElement, ...elements.slice(index)];
        }
      }

      if (side === ElementSide.Left) {
        const droppableRow = droppablePage?.rows.find(row => row.id === rowId);
        const columnIndex = droppableRow?.columns.findIndex(column => column.id === columnId);
        if (movedElement && droppableRow) {
          droppableRow.columns = [
            ...droppableRow.columns.slice(0, columnIndex),
            {
              type: PagePartType.Column,
              baseColumns: 4,
              id: generateId(),
              elements: [...movedElement],
            },
            ...droppableRow.columns.slice(columnIndex),
          ];
        }
      }

      if (side === ElementSide.Right) {
        const droppableRow = droppablePage?.rows.find(row => row.id === rowId);
        const columnIndex = droppableRow?.columns.findIndex(column => column.id === columnId);
        if (movedElement && droppableRow) {
          droppableRow.columns = [
            ...droppableRow.columns.slice(0, Number(columnIndex) + 1),
            {
              type: PagePartType.Column,
              baseColumns: 4,
              id: generateId(),
              elements: [...movedElement],
            },
            ...droppableRow.columns.slice(Number(columnIndex) + 1),
          ];
        }
      }
    },
    moveSectionItem(state, action) {
      if (
        !state.selectedItem ||
        state.selectedItem.type === ElementType.Header ||
        state.selectedItem.type === ElementType.Footer ||
        state.selectedItem.type === PagePartType.Page
      )
        return;
      const { index: draggableIndex, pageId: draggablePageId } = state.selectedItem;
      const { pageId, index, isTop } = action.payload;
      const sourcePage = findPageById(state.templateJson.pages, draggablePageId);

      const destinationPage = findPageById(state.templateJson.pages, pageId);

      if (sourcePage && pageId === draggablePageId) {
        const rows = sourcePage.rows.slice();
        const row = rows.splice(draggableIndex, 1)[0];

        if (isTop) {
          sourcePage.rows = [...rows.splice(0, index - 1), row, ...rows];
        } else {
          if (draggableIndex < index) sourcePage.rows = [...rows.splice(0, index), row, ...rows];
          else sourcePage.rows = [...rows.splice(0, index + 1), row, ...rows];
        }
      } else if (sourcePage && pageId !== draggablePageId && destinationPage) {
        const rows = sourcePage.rows.slice();
        const row = rows.splice(draggableIndex, 1)[0];
        const destinationRows = destinationPage.rows.slice();

        sourcePage.rows = rows;

        if (isTop) {
          destinationPage.rows = [...destinationRows.splice(0, index), row, ...destinationRows];
        } else {
          destinationPage.rows = [...destinationRows.splice(0, index + 1), row, ...destinationRows];
        }
      }
      state.selectedItem = null;
    },

    duplicateRowContentBlock(state, action) {
      const { pageId, side, rowId } = action.payload;

      const page = findPageById(state.templateJson.pages, pageId);

      if (page) {
        const existingRow = page.rows.find(row => row.id === rowId);

        if (existingRow) {
          const newRow = { ...existingRow, id: `row-${generateId()}` };
          const newRowIndex = page?.rows.length;

          const duplicatedElements = existingRow.columns.map(column => ({
            ...column,
            elements: column.elements.map(element => ({
              ...element,
              id: `${generateId()}`,
            })),
          }));

          newRow.columns = newRow.columns.map((column, index) => ({
            ...column,
            id: `column-${generateId()}`,
            elements: duplicatedElements[index].elements,
          }));

          if (side === ElementSide.Top) {
            page.rows = [...page.rows.slice(0, newRowIndex), newRow, ...page.rows.slice(newRowIndex)];
          }
          if (side === ElementSide.Bottom) {
            page.rows = [...page.rows.slice(0, newRowIndex + 1), newRow, ...page.rows.slice(newRowIndex + 1)];
          }
        }
      }
    },

    duplicateElementContentBlock(state, action) {
      const { pageId, rowId, columnId, itemId } = action.payload;
      const page = findPageById(state.templateJson.pages, pageId);

      if (page) {
        const rowContainer = page.rows.find(row => row.id === rowId);

        if (rowContainer) {
          const columnIndex = rowContainer.columns.findIndex(column => column.id === columnId);

          if (columnIndex !== -1) {
            const originalElement = rowContainer.columns[columnIndex].elements.find(element => element.id === itemId);
            if (originalElement) {
              const duplicatedElementId = generateId();
              const duplicatedElement = {
                ...originalElement,
                id: duplicatedElementId,
              };
              if (
                isCheckboxElement(originalElement) ||
                isTextFieldElement(originalElement) ||
                isDateElement(originalElement) ||
                isRadioButtonElement(originalElement) ||
                isDropdownElement(originalElement) ||
                isSignatureElement(originalElement)
              ) {
                const selectAction = {
                  ...action,
                  payload: {
                    recipientId: originalElement.recipient.id,
                    elementId: duplicatedElementId,
                  },
                };

                documentSlice.caseReducers.addElementIdToRecipient(state, selectAction);
              }
              const duplicateElementIndex = rowContainer.columns[columnIndex].elements.indexOf(originalElement) + 1;

              rowContainer.columns[columnIndex].elements.splice(duplicateElementIndex, 0, duplicatedElement);
              const selectAction = {
                ...action,
                payload: {
                  data: {
                    columnId: columnId,
                    pageId: pageId,
                    data: duplicatedElement,
                    type: 'ELEMENT',
                    rowId: rowId,
                    index: duplicateElementIndex,
                  },
                },
              };
              documentSlice.caseReducers.saveSelectedItem(state, selectAction);
            }
          }
        }
      }
    },
    toggleSidebar(state) {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    updateSavingMethod(state, action) {
      state.savingMethod = action.payload;
    },
    updatePage<T extends keyof Page>(state: DocumentTemplate, action: { payload: ActionPayload<T> }) {
      const { property, value } = action.payload;
      if (!state.selectedItem) return;
      if (state.selectedItem.type === PagePartType.Page) {
        const { pageId } = state.selectedItem;
        const page = findPageById(state.templateJson.pages, pageId);
        if (page) {
          page[property] = value;
          state.selectedItem.data[property] = value;
        }
      }
    },
    togglePageOrientation(state, action) {
      const newOrientation = action.payload;
      state.templateJson.isOrientedHorizontally = newOrientation;
    },
    togglePageOrientationById(state, action: PayloadAction<{ pageId: string }>) {
      const page = state.templateJson.pages.find(page => page.id === action.payload.pageId);
      if (page) {
        page.isOrientedHorizontally = !page.isOrientedHorizontally;
      }
    },
    removePageById(state, action) {
      const id = action.payload;
      const newPages = state.templateJson.pages.filter(page => page.id !== id);
      state.templateJson.pages = newPages;
    },
    updatePageById<T extends keyof Page>(state: DocumentTemplate, action: { payload: ActionPayload<T> }) {
      const { id, property, value } = action.payload;
      if (id) {
        const page = findPageById(state.templateJson.pages, id);
        if (page) {
          page[property] = value;
        }
      }
    },
  },
  extraReducers: {},
});

export default documentSlice.reducer;

export const selectTemplateDocument = (state: RootState) => state.document.templateJson;
export const selectDocumentName = (state: RootState) => state.document.name;
export const selectNoteTitle = (state: RootState) => state.document.noteTitle;
export const selectNoteContent = (state: RootState) => state.document.noteContent;
export const selectDocumentType = (state: RootState) => state.document.type;
export const selectInitialDocument = (state: RootState) => state.document.initialDocument;
export const selectRecipients = (state: RootState) => state.document.templateJson.recipients;
export const selectSalesforceIds = (state: RootState) => state.document.templateJson.salesforceIds;
export const selectActiveProductById = (
  state: RootState,
  pageId: string,
  rowId: string,
  columnId: string,
  elementId: string
) => {
  const originPage = findPageById(state.document.templateJson.pages, pageId);

  if (!originPage) {
    return;
  }

  const rowIndex = originPage.rows.findIndex(row => row.id === rowId);

  if (rowIndex === -1) {
    return;
  }

  const column = originPage.rows[rowIndex].columns.find(column => column.id === columnId);

  if (column) {
    const pricingTable = column.elements.find(element => element.id === elementId);

    if (pricingTable && isPricingTableElement(pricingTable)) {
      return pricingTable.content.activeProduct;
    }
  }
};
export const selectActiveSidebarById = (
  state: RootState,
  pageId: string,
  rowId: string,
  columnId: string,
  elementId: string
) => {
  const originPage = findPageById(state.document.templateJson.pages, pageId);

  if (!originPage) {
    return;
  }

  const rowIndex = originPage.rows.findIndex(row => row.id === rowId);

  if (rowIndex === -1) {
    return;
  }

  const column = originPage.rows[rowIndex].columns.find(column => column.id === columnId);

  if (column) {
    const elementSelected = column.elements.find(element => element.id === elementId);
    if (
      elementSelected &&
      (isQuoteAcceptanceInfoTableElement(elementSelected) || isApprovalTableElement(elementSelected))
    ) {
      return elementSelected.content.activeSidebar;
    }
  }
};

export const selectSelectedItem = (state: RootState) => state.document.selectedItem;

export const selectSelectedElement = (state: RootState) => {
  const selectedItem = state.document.selectedItem;
  return selectedItem?.type === PagePartType.Element ? selectedItem : null;
};

export const selectSelectedHeader = (state: RootState) => {
  const selectedHeader = state.document.selectedItem;
  return selectedHeader?.type === ElementType.Header ? selectedHeader : null;
};

export const selectSelectedPage = (state: RootState) => {
  const selectedPage = state.document.selectedItem;
  return selectedPage?.type === PagePartType.Page ? selectedPage : null;
};

export const selectSelectedFooter = (state: RootState) => {
  const selectedFooter = state.document.selectedItem;
  return selectedFooter?.type === ElementType.Footer ? selectedFooter : null;
};
export const selectPageStyles = (state: RootState, pageId: string) => {
  const page = findPageById(state.document.templateJson.pages, pageId);
  if (page) {
    return page;
  }
};
export const selectSavingMethod = (state: RootState) => state.document.savingMethod;
export const selectPageOrientationById = (state: RootState, pageId: string) => {
  const page = state.document.templateJson.pages.find(page => page.id === pageId);
  return page?.isOrientedHorizontally ? page.isOrientedHorizontally : false;
};

export const {
  updateContentFooterById,
  updateNoteContent,
  updateDocumentName,
  updateNoteTitle,
  updateDocumentType,
  toggleIndividualVisibilityHeader,
  toggleIndividualVisibilityFooter,
  toggleAllVisibilityHeader,
  toggleAllVisibilityFooter,
  addPage,
  addPageSection,
  updateTemplateJson,
  updateSelectedRecipient,
  updateRecipients,
  updateTemplateJsonSalesforceIds,
  removeElementIdFromRecipients,
  addElementIdToRecipient,
  updateInitialDocument,
  addRowContentBlock,
  saveSelectedItem,
  moveSectionItem,
  clearSelectedItem,
  moveElementitem,
  deleteRowById,
  updateContentHeaderById,
  deleteColumnById,
  deleteElementById,
  addElementContentBlock,
  updateContentElementById,
  resetState,
  duplicateRowContentBlock,
  duplicateElementContentBlock,
  toggleSidebar,
  updateElementById,
  updateSavingMethod,
  updatePage,
  togglePageOrientation,
  togglePageOrientationById,
  removePageById,
  updatePageById,
} = documentSlice.actions;

const findPageById = (pages: Page[], pageId: string) => {
  return pages.find(page => page.id === pageId);
};
